import React from 'react';
// Import useJourney from the context module directly (not the journeys barrel, './'):
// the barrel re-exports JourneyProvider → journey manifest → renderers, which forms an
// import cycle. ChatMessage reads it the same way.
import { useJourney } from './context';
import { createLazyComponent } from '../shared/createLazyComponent';
import { DrawerLoading } from '../shell/DrawerLoading';

// Build + cache the lazy content wrapper per journey content descriptor. Cached at module
// scope, keyed by the descriptor object, so (a) a journey swap that keeps the same content
// (e.g. the paint journey reuses the default's content) doesn't remount, and (b) the
// wrapper is never created during render (avoids react/no-unstable-nested-components and a
// fresh lazy loader every render). The descriptor is `{ load, exportName, label? }` — a
// dynamic-import loader, NOT a component, so the journey manifest never statically pulls
// the (heavy) content into the warm-while-closed provider chunk (WS-11.2, option A). The
// Shell owns the loading placeholder (chrome); the manifest only says where content loads.
const lazyContentByDescriptor = new WeakMap();
const getLazyContent = (descriptor) => {
  if (!lazyContentByDescriptor.has(descriptor)) {
    lazyContentByDescriptor.set(descriptor, createLazyComponent(
      descriptor.load,
      descriptor.exportName,
      { label: descriptor.label ?? 'Journey content', placeholder: <DrawerLoading /> },
    ));
  }
  return lazyContentByDescriptor.get(descriptor);
};

// The Shell's body slot: renders the ACTIVE journey's content, resolved via useJourney and
// lazily loaded (with the Shell's drawer-loading placeholder) while its chunk resolves. The
// Shell hosts whatever content the resolved journey provides — it imports no specific
// journey (WS-11.2).
export const JourneyContent = () => {
  const journey = useJourney();
  const content = journey?.content;
  if (!content) return null;
  const LazyJourneyContent = getLazyContent(content);
  return <LazyJourneyContent />;
};
